import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Settings, Eye, EyeOff, GripVertical } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface Column {
  id: string
  name: string
  label: string
  type: 'text' | 'number' | 'date' | 'boolean' | 'select'
  required: boolean
  visible: boolean
  order: number
}

interface DomainColumnManagerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onColumnsUpdated: (columns: Column[]) => void
}

export function DomainColumnManagerDialog({ open, onOpenChange, onColumnsUpdated }: DomainColumnManagerDialogProps) {
  const { toast } = useToast()
  
  const defaultColumns: Column[] = [
    { id: 'domain', name: 'domain', label: 'Domain', type: 'text', required: true, visible: true, order: 1 },
    { id: 'client_name', name: 'client_name', label: 'Client', type: 'text', required: false, visible: true, order: 2 },
    { id: 'status', name: 'status', label: 'Status', type: 'select', required: false, visible: true, order: 3 },
    { id: 'domain_registrar', name: 'domain_registrar', label: 'Domain Registrar', type: 'text', required: false, visible: true, order: 4 },
    { id: 'mailbox_provider', name: 'mailbox_provider', label: 'Mailbox Provider', type: 'text', required: false, visible: true, order: 5 },
    { id: 'email_provider', name: 'email_provider', label: 'Email Provider', type: 'text', required: false, visible: true, order: 6 },
    { id: 'total_mailboxes', name: 'total_mailboxes', label: 'Mailboxes (Total)', type: 'number', required: false, visible: true, order: 7 },
    { id: 'active_mailboxes', name: 'active_mailboxes', label: 'Mailboxes (Active)', type: 'number', required: false, visible: true, order: 8 },
    { id: 'total_emails_sent', name: 'total_emails_sent', label: 'Emails Sent', type: 'number', required: false, visible: true, order: 9 },
    { id: 'total_replies', name: 'total_replies', label: 'Replies', type: 'number', required: false, visible: true, order: 10 },
    { id: 'total_bounces', name: 'total_bounces', label: 'Bounces', type: 'number', required: false, visible: true, order: 11 },
    { id: 'reply_rate', name: 'reply_rate', label: 'Reply %', type: 'number', required: false, visible: false, order: 12 },
    { id: 'health_score', name: 'health_score', label: 'Health Score', type: 'number', required: false, visible: false, order: 13 },
    { id: 'reputation_score', name: 'reputation_score', label: 'Reputation Score', type: 'number', required: false, visible: false, order: 14 },
    { id: 'bounce_rate', name: 'bounce_rate', label: 'Bounce Rate', type: 'number', required: false, visible: false, order: 15 },
    { id: 'purchase_date', name: 'purchase_date', label: 'Purchase Date', type: 'date', required: false, visible: false, order: 16 },
    { id: 'renewal_date', name: 'renewal_date', label: 'Renewal Date', type: 'date', required: false, visible: false, order: 17 },
    { id: 'expiry_date', name: 'expiry_date', label: 'Expiry Date', type: 'date', required: false, visible: false, order: 18 },
    { id: 'auto_renewal', name: 'auto_renewal', label: 'Auto Renewal', type: 'boolean', required: false, visible: false, order: 19 },
    { id: 'cost', name: 'cost', label: 'Cost', type: 'number', required: false, visible: false, order: 20 },
    { id: 'notes', name: 'notes', label: 'Notes', type: 'text', required: false, visible: false, order: 21 },
    { id: 'smartlead_status', name: 'smartlead_status', label: 'In Smartlead', type: 'boolean', required: false, visible: false, order: 22 },
    { id: 'created_at', name: 'created_at', label: 'Created At', type: 'date', required: false, visible: false, order: 23 },
    { id: 'updated_at', name: 'updated_at', label: 'Updated At', type: 'date', required: false, visible: false, order: 24 },
  ]

  const [columns, setColumns] = useState<Column[]>(defaultColumns)
  const [draggedItem, setDraggedItem] = useState<Column | null>(null)

  const getColumnStorageKey = () => `domain-column-config`

  // Load column configurations from localStorage
  useEffect(() => {
    const loadColumnConfig = () => {
      try {
        const savedConfig = localStorage.getItem(getColumnStorageKey())
        if (savedConfig) {
          const parsedConfig = JSON.parse(savedConfig)
          // Merge saved config with default columns to handle new columns
          const mergedColumns = defaultColumns.map(defaultCol => {
            const savedCol = parsedConfig.find((saved: Column) => saved.id === defaultCol.id)
            return savedCol ? { ...defaultCol, ...savedCol } : defaultCol
          })
          setColumns(mergedColumns)
        } else {
          setColumns(defaultColumns)
        }
      } catch (error) {
        console.error('Error loading column configuration:', error)
        setColumns(defaultColumns)
      }
    }

    if (open) {
      loadColumnConfig()
    }
  }, [open])

  const saveColumnConfig = (updatedColumns: Column[]) => {
    try {
      localStorage.setItem(getColumnStorageKey(), JSON.stringify(updatedColumns))
      onColumnsUpdated(updatedColumns)
      toast({
        title: "Success",
        description: "Column configuration saved successfully"
      })
    } catch (error) {
      console.error('Error saving column configuration:', error)
      toast({
        title: "Error",
        description: "Failed to save column configuration",
        variant: "destructive"
      })
    }
  }

  const toggleColumnVisibility = (columnId: string) => {
    const updatedColumns = columns.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    )
    setColumns(updatedColumns)
  }

  const handleSave = () => {
    saveColumnConfig(columns)
    onOpenChange(false)
    // Force a page reload to ensure the table updates immediately
    window.location.reload()
  }

  const handleReset = () => {
    setColumns(defaultColumns)
    saveColumnConfig(defaultColumns)
  }

  const handleHideAll = () => {
    const updatedColumns = columns.map(col => ({ 
      ...col, 
      visible: col.required // Keep required columns visible, hide all others
    }))
    setColumns(updatedColumns)
  }

  const handleShowAll = () => {
    const updatedColumns = columns.map(col => ({ ...col, visible: true }))
    setColumns(updatedColumns)
  }

  const handleDragStart = (e: React.DragEvent, column: Column) => {
    setDraggedItem(column)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, targetColumn: Column) => {
    e.preventDefault()
    
    if (!draggedItem || draggedItem.id === targetColumn.id) {
      setDraggedItem(null)
      return
    }

    const draggedIndex = columns.findIndex(col => col.id === draggedItem.id)
    const targetIndex = columns.findIndex(col => col.id === targetColumn.id)

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null)
      return
    }

    const newColumns = [...columns]
    const [removed] = newColumns.splice(draggedIndex, 1)
    newColumns.splice(targetIndex, 0, removed)

    // Update order property based on new position
    const updatedColumns = newColumns.map((col, index) => ({
      ...col,
      order: index + 1
    }))

    setColumns(updatedColumns)
    setDraggedItem(null)
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
  }

  const visibleCount = columns.filter(col => col.visible).length
  const hiddenCount = columns.filter(col => !col.visible).length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Manage Domain Columns
          </DialogTitle>
        </DialogHeader>

        {/* Bulk Actions - Moved to top */}
        <div className="flex items-center justify-between border-b pb-4">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleHideAll}>
              Hide All
            </Button>
            <Button variant="outline" onClick={handleShowAll}>
              Show All
            </Button>
            <Button variant="outline" onClick={handleReset}>
              Reset to Default
            </Button>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {visibleCount} visible
            </div>
            <div className="flex items-center gap-2">
              <EyeOff className="w-4 h-4" />
              {hiddenCount} hidden
            </div>
          </div>
        </div>

        <div className="space-y-6">

          {/* Columns List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Domain Table Columns</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                {columns
                  .sort((a, b) => a.order - b.order)
                  .map((column, index) => (
                    <div
                      key={column.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, column)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, column)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center justify-between p-3 rounded-lg border bg-card cursor-move transition-colors hover:bg-accent/50 ${
                        draggedItem?.id === column.id ? 'opacity-50 scale-95' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab active:cursor-grabbing" />
                        <div className="flex-1">
                          <div className="font-medium">{column.label}</div>
                          <div className="text-sm text-muted-foreground">
                            {column.name} • {column.type}
                            {column.required && (
                              <span className="ml-2 text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                                Required
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={column.visible}
                          onCheckedChange={() => toggleColumnVisibility(column.id)}
                          disabled={column.required}
                        />
                        <span className="text-sm text-muted-foreground w-16">
                          {column.visible ? 'Visible' : 'Hidden'}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}